<template>
  <article class="tb-card" :class="'tb-card--' + priorityKey" @click="$emit('open', ticket)">
    <header class="tb-card__head">
      <button type="button" class="tb-card__id" @click.stop="$emit('copy-id', ticket.Id)"
              v-b-tooltip.hover.top="'Скопировать ID'">
        #{{ ticket.Id }}
      </button>

      <span class="tb-prio" :class="'tb-prio--' + priorityKey">
        <span class="tb-prio__dot"></span>{{ priorityText }}
      </span>

      <span class="tb-card__spacer"></span>

      <button type="button" class="tb-card__icon" :class="{ 'has-unread': unread > 0 }"
              @click.stop="$emit('open', ticket)"
              v-b-tooltip.hover.top="unread > 0 ? `Непрочитанных: ${unread}` : 'Открыть чат'">
        <i class="fa fa-comments-o"></i>
        <span v-if="unread > 0" class="tb-card__badge">{{ unread > 99 ? '99+' : unread }}</span>
      </button>

      <button type="button" class="tb-card__icon" @click.stop="$emit('open-history', ticket)"
              v-b-tooltip.hover.top="'История тикета'">
        <i class="fa fa-history"></i>
      </button>
    </header>

    <h4 class="tb-card__title" :title="ticket.Title">{{ ticket.Title || 'Без темы' }}</h4>

    <div v-if="ticket.Type || ticket.Category || ticket.Source" class="tb-card__tags">
      <span v-if="ticket.Type" class="tb-tag">{{ ticket.Type }}</span>
      <span v-if="ticket.Category" class="tb-tag">{{ ticket.Category }}</span>
      <span v-if="ticket.Source" class="tb-tag tb-tag--source">{{ sourceText }}</span>
    </div>

    <div class="tb-card__action">
      <i class="fa fa-bolt"></i>
      <span>{{ (ticket.action && (ticket.action.title || ticket.action.name)) || 'Нет действия' }}</span>
    </div>

    <footer class="tb-card__foot">
      <button v-if="ticket.User" type="button" class="tb-user" @click.stop="$emit('open-user', ticket.UserId)"
              v-b-tooltip.hover.top="ticket.UserEmail || ticket.User">
        <span class="tb-user__avatar">{{ initials }}</span>
        <span class="tb-user__name">{{ ticket.User }}</span>
      </button>
      <span v-else class="tb-user tb-user--empty">
        <span class="tb-user__avatar">?</span>
        <span class="tb-user__name">без автора</span>
      </span>

      <span class="tb-card__date" :title="fullDate">{{ shortDate }}</span>
    </footer>
  </article>
</template>

<script>
const PRIORITY_TEXT = {
  low: 'Низкий',
  medium: 'Средний',
  high: 'Высокий',
  urgent: 'Срочный',
};

const SOURCE_TEXT = {
  website: 'Web-виджет',
  telegram: 'Telegram',
};

export default {
  name: 'TicketBoardCardTemplate',
  props: {
    ticket: {
      type: Object,
      required: true,
    },
  },
  computed: {
    priorityKey() {
      return this.ticket.Priority || 'none';
    },

    priorityText() {
      return PRIORITY_TEXT[this.ticket.Priority] || this.ticket.Priority || 'Без приоритета';
    },

    sourceText() {
      return SOURCE_TEXT[this.ticket.Source] || this.ticket.Source;
    },

    unread() {
      return Number(this.ticket.UnreadMessageCount) || 0;
    },

    initials() {
      const name = this.ticket.User;
      if (!name) return '?';
      const parts = String(name).trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return String(name).substring(0, 2).toUpperCase();
    },

    shortDate() {
      const date = this.parsed;
      if (!date) return '';
      const days = Math.round((Date.now() - date.getTime()) / 86400000);
      if (days <= 0) {
        const pad = (n) => String(n).padStart(2, '0');
        return `сегодня ${pad(date.getHours())}:${pad(date.getMinutes())}`;
      }
      if (days === 1) return 'вчера';
      if (days < 30) return `${days} дн. назад`;
      return date.toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit', year: 'numeric'});
    },

    fullDate() {
      const date = this.parsed;
      return date ? date.toLocaleString('ru-RU') : '';
    },

    parsed() {
      const raw = this.ticket.TicketDate;
      if (!raw) return null;
      const date = new Date(String(raw).replace(' ', 'T'));
      return isNaN(date.getTime()) ? null : date;
    },
  },
};
</script>

<style scoped lang="scss">
.tb-card {
  background: var(--tb-bg);
  border: 1px solid var(--tb-border);
  border-left: 3px solid var(--tb-muted);
  border-radius: 12px;
  padding: 11px 12px;
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease;

  &:hover {
    border-color: var(--tb-primary);
    box-shadow: 0 4px 14px rgba(30, 38, 60, .1);
    transform: translateY(-1px);
  }

  &--urgent { border-left-color: var(--tb-danger); }
  &--high { border-left-color: var(--tb-live); }
  &--medium { border-left-color: var(--tb-warn); }
  &--low { border-left-color: var(--tb-ok); }

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
  }

  &__spacer { flex: 1 1 auto; }

  &__id {
    border: 0;
    background: var(--tb-soft);
    color: var(--tb-muted);
    padding: 2px 7px;
    border-radius: 6px;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    cursor: pointer;

    &:hover { background: var(--tb-primary-soft); color: var(--tb-primary); }
  }

  &__icon {
    position: relative;
    width: 26px;
    height: 26px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--tb-muted);
    font-size: 13px;
    cursor: pointer;

    &:hover { background: var(--tb-soft); color: var(--tb-primary); }

    &.has-unread { color: var(--tb-primary); }
  }

  &__badge {
    position: absolute;
    top: -3px;
    right: -3px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: var(--tb-danger);
    color: #fff;
    font-size: 9.5px;
    font-weight: 700;
    line-height: 16px;
    box-shadow: 0 0 0 2px var(--tb-bg);
  }

  &__title {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
    color: var(--tb-text);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 8px;
  }

  &__action {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    border-radius: 8px;
    background: var(--tb-soft);
    font-size: 11.5px;
    color: var(--tb-text);

    i { color: var(--tb-primary); font-size: 11px; }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 9px;
  }

  &__date {
    font-size: 11px;
    color: var(--tb-muted);
    white-space: nowrap;
  }
}

.tb-prio {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 600;
  color: var(--tb-muted);
  white-space: nowrap;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  &--urgent { color: var(--tb-danger); }
  &--high { color: var(--tb-live); }
  &--medium { color: var(--tb-warn); }
  &--low { color: var(--tb-ok); }
}

.tb-tag {
  padding: 2px 7px;
  border-radius: 6px;
  background: var(--tb-soft-2);
  color: var(--tb-muted);
  font-size: 10.5px;
  font-weight: 500;
  white-space: nowrap;

  &--source { background: var(--tb-primary-soft); color: var(--tb-primary); }
}

.tb-user {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  border: 0;
  background: none;
  padding: 0;
  cursor: pointer;

  &--empty { cursor: default; opacity: .7; }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 7px;
    background: var(--tb-primary-soft);
    color: var(--tb-primary);
    font-size: 9.5px;
    font-weight: 700;
    flex: 0 0 auto;
  }

  &__name {
    font-size: 11.5px;
    color: var(--tb-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:not(&--empty):hover &__name { color: var(--tb-primary); }
}
</style>
